<!--
  Partner — lista.

  Stesso impianto di clienti e driver: caricamento a pagine dal server (oltre
  1000 record il backend tronca senza segnalarlo), filtri e ordinamento
  eseguiti dal database.

  TIPO PARTNER — a schema è il booleano `is_restaurant`:
    false → automotive    true → notturno (ex "restaurant")

  La colonna "Servizi" mostra `booking_type` e `journey_type` perché non sono
  dettagli tecnici: determinano cosa viene fatturato al partner (COD3).
-->
<template>
  <div class="prt">
    <div class="prt__head">
      <div>
        <h1 class="prt__title">
          Partner
        </h1>
        <p class="prt__sub">
          Anagrafica partner, sedi e servizi attivi.
        </p>
      </div>
      <div class="prt__headActions">
        <el-input
          v-model="search"
          class="prt__search"
          placeholder="Cerca ragione sociale, P.IVA, mail…"
          clearable
          @input="onSearchInput"
        />
        <router-link
          to="/add-partner"
          class="routerLink"
        >
          <el-button type="primary">
            Nuovo partner
          </el-button>
        </router-link>
      </div>
    </div>

    <!-- le card filtrano per tipo -->
    <div class="prt__kpis">
      <button
        v-for="k in kpis"
        :key="k.key"
        type="button"
        class="prt__kpi"
        :class="{ 'is-active': type === k.key }"
        @click="setType(k.key)"
      >
        <span class="prt__kpiNum">{{ k.value === null ? '—' : k.value }}</span>
        <span class="prt__kpiLbl">{{ k.label }}</span>
      </button>
    </div>

    <div class="prt__card">
      <div class="prt__toolbar">
        <span class="prt__hint">
          Clicca una riga per aprire la scheda del partner.
        </span>
        <div class="prt__spacer" />
        <el-button
          text
          @click="resetFilters"
        >
          Azzera filtri
        </el-button>
      </div>

      <el-table
        v-loading="loading"
        :data="rows"
        style="width: 100%"
        empty-text="Nessun partner corrisponde ai filtri"
        @row-click="openDetail"
        @sort-change="onSortChange"
      >
        <el-table-column
          label="Partner"
          prop="company_name"
          sortable="custom"
          min-width="250"
        >
          <template #default="scope">
            <div class="prt__who">
              <span class="prt__avatar">{{ ini(scope.row) }}</span>
              <div>
                <div class="prt__name">
                  {{ scope.row.company_name || '—' }}
                </div>
                <div class="prt__meta">
                  {{ scope.row.mail || '—' }}
                </div>
              </div>
            </div>
          </template>
        </el-table-column>

        <!-- Ridondante quando si sta già filtrando per tipo: serve solo sulla
             tab "Totale", dove automotive e notturno sono mescolati. -->
        <el-table-column
          v-if="!type"
          label="Tipo"
          width="150"
        >
          <template #default="scope">
            <span
              class="prt__type"
              :class="isNotturno(scope.row) ? 'is-notturno' : 'is-automotive'"
            >
              {{ isNotturno(scope.row) ? 'Notturno' : 'Automotive' }}
            </span>
          </template>
        </el-table-column>

        <el-table-column
          label="P. IVA"
          min-width="140"
        >
          <template #default="scope">
            <span class="prt__num">{{ scope.row.piva || '—' }}</span>
          </template>
        </el-table-column>

        <el-table-column
          label="Sedi"
          min-width="200"
        >
          <template #default="scope">
            <div
              v-if="scope.row.headquarters?.length"
              class="prt__hqs"
            >
              <span class="prt__hqCount">{{ scope.row.headquarters.length }}</span>
              <span class="prt__hqNames">{{ hqNames(scope.row) }}</span>
            </div>
            <span
              v-else
              class="prt__dash"
            >—</span>
          </template>
        </el-table-column>

        <el-table-column
          label="Servizi"
          min-width="170"
        >
          <template #default="scope">
            <div class="prt__svcs">
              <span
                v-if="scope.row.booking_type"
                class="prt__svc is-booking"
              >Booking</span>
              <span
                v-if="scope.row.journey_type"
                class="prt__svc is-journey"
              >Spost. interni</span>
              <span
                v-if="!scope.row.booking_type && !scope.row.journey_type"
                class="prt__dash"
              >—</span>
            </div>
          </template>
        </el-table-column>

        <el-table-column
          label="Provvigione"
          width="120"
          align="right"
        >
          <template #default="scope">
            <span class="prt__num">
              {{ scope.row.provvigione != null ? scope.row.provvigione + '%' : '—' }}
            </span>
          </template>
        </el-table-column>

        <el-table-column
          width="120"
          align="right"
          label="Azioni"
          fixed="right"
        >
          <template #default="scope">
            <el-button
              size="small"
              type="primary"
              @click.stop="openDetail(scope.row)"
            >
              Dettagli
            </el-button>
          </template>
        </el-table-column>
      </el-table>

      <div class="prt__foot">
        <span class="prt__footInfo">
          {{ total }} partner
          <template v-if="pageCount > 1">· pagina {{ page }} di {{ pageCount }}</template>
        </span>
        <el-pagination
          background
          layout="prev, pager, next"
          :page-size="pageSize"
          :total="total"
          :current-page="page"
          @current-change="onPageChange"
        />
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import partnerAPI from '../../services/partnerAPI.js';

const PAGE_SIZE = 25;

export default {
  name: 'PartnerPage',

  setup() {
    const router = useRouter();

    const rows = ref([]);
    const loading = ref(false);
    const page = ref(1);
    const total = ref(0);
    const pageCount = ref(1);
    const search = ref('');
    const type = ref('');
    const sort = ref('');
    const counts = ref({ automotive: null, notturno: null, '': null });

    const kpis = computed(() => [
      { key: 'automotive', label: 'Automotive', value: counts.value.automotive },
      { key: 'notturno', label: 'Notturno', value: counts.value.notturno },
      { key: '', label: 'Totale', value: counts.value[''] },
    ]);

    const load = async () => {
      loading.value = true;
      try {
        const res = await partnerAPI.getPartnersPaged({
          page: page.value,
          limit: PAGE_SIZE,
          search: search.value,
          type: type.value,
          sort: sort.value,
        });
        rows.value = res.rows;
        total.value = res.total;
        pageCount.value = res.pageCount || 1;
      } catch (e) {
        console.error('Caricamento partner fallito:', e);
        rows.value = [];
        total.value = 0;
      } finally {
        loading.value = false;
      }
    };

    const loadCounts = async () => {
      const keys = ['automotive', 'notturno', ''];
      const res = await Promise.allSettled(keys.map((k) => partnerAPI.countPartners(k)));
      res.forEach((r, i) => {
        if (r.status === 'fulfilled') counts.value[keys[i]] = r.value;
      });
    };

    const reload = () => { page.value = 1; load(); };

    const setType = (key) => {
      if (type.value === key) return;
      type.value = key;
      reload();
    };

    const onPageChange = (p) => { page.value = p; load(); };

    // L'ordinamento lo fa il database: ordinare in memoria riguarderebbe solo
    // le 25 righe già scaricate, non l'intero elenco.
    const onSortChange = ({ prop, order }) => {
      sort.value = prop && order ? `${prop},${order === 'ascending' ? 'ASC' : 'DESC'}` : '';
      reload();
    };

    let timer = null;
    const onSearchInput = () => {
      clearTimeout(timer);
      timer = setTimeout(reload, 350);
    };

    const resetFilters = () => {
      search.value = '';
      type.value = '';
      sort.value = '';
      reload();
    };

    const openDetail = (row) => {
      if (row?.id) router.push(`/partner/${row.id}`);
    };

    const ini = (p) => {
      const words = (p?.company_name || '').trim().split(/\s+/).filter(Boolean);
      if (!words.length) return '?';
      return (words[0][0] + (words[1]?.[0] || '')).toUpperCase();
    };

    const hqNames = (p) => {
      const names = (p.headquarters || []).map((h) => h.city || h.name).filter(Boolean);
      return names.slice(0, 2).join(', ') + (names.length > 2 ? ` +${names.length - 2}` : '');
    };

    onMounted(() => { load(); loadCounts(); });

    return {
      rows, loading, page, total, pageCount, search, type,
      kpis, pageSize: PAGE_SIZE,
      setType, onPageChange, onSortChange, onSearchInput, resetFilters,
      openDetail, ini, hqNames,
      isNotturno: partnerAPI.isNotturno,
    };
  },
};
</script>

<style scoped>
.prt { padding: 4px 0 32px; }

.prt__head {
  display: flex; align-items: flex-end; justify-content: space-between;
  gap: 16px; flex-wrap: wrap; margin-bottom: 18px;
}
.prt__title { margin: 0; font-size: 22px; color: var(--text-primary); }
.prt__sub { margin: 4px 0 0; font-size: 13px; color: var(--text-tertiary); }
.prt__headActions { display: flex; gap: 10px; flex-wrap: wrap; align-items: center; }
.prt__search { width: 280px; }

.prt__kpis {
  display: grid; grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 12px; margin-bottom: 16px;
}
@media (max-width: 700px) { .prt__kpis { grid-template-columns: 1fr; } }
.prt__kpi {
  text-align: left; background: var(--bg-primary);
  border: 1px solid var(--border-color); border-radius: var(--radius-lg);
  padding: 13px 15px; cursor: pointer; font-family: inherit;
  transition: border-color var(--transition-fast);
}
.prt__kpi:hover { border-color: var(--brand-violet); }
.prt__kpi.is-active {
  border-color: var(--brand-violet);
  box-shadow: inset 0 0 0 1px var(--brand-violet);
}
.prt__kpiNum {
  display: block; font-size: 24px; font-weight: 750; letter-spacing: -0.02em;
  font-variant-numeric: tabular-nums; color: var(--text-primary);
}
.prt__kpiLbl {
  display: block; margin-top: 2px; font-size: 11.5px; font-weight: 600;
  letter-spacing: 0.06em; text-transform: uppercase; color: var(--text-tertiary);
}

.prt__card {
  background: var(--bg-primary); border: 1px solid var(--border-color);
  border-radius: var(--radius-lg); overflow: hidden;
}
.prt__toolbar {
  display: flex; align-items: center; gap: 12px; flex-wrap: wrap;
  padding: 12px 14px; border-bottom: 1px solid var(--border-color);
}
.prt__hint { font-size: 12.5px; color: var(--text-tertiary); }
.prt__spacer { flex: 1; }

.prt__who { display: flex; align-items: center; gap: 10px; }
.prt__avatar {
  width: 32px; height: 32px; flex: none; border-radius: 50%;
  display: grid; place-items: center; background: var(--avatar-bg);
  color: #fff; font-size: 11px; font-weight: 700;
}
.prt__name { font-weight: 600; color: var(--text-primary); }
.prt__meta { font-size: 12px; color: var(--text-tertiary); }
.prt__num { font-variant-numeric: tabular-nums; }
.prt__dash { color: var(--text-tertiary); }

.prt__type {
  display: inline-block; white-space: nowrap;
  font-size: 11px; font-weight: 700; letter-spacing: 0.03em;
  text-transform: uppercase; border-radius: 5px; padding: 2px 8px;
}
.prt__type.is-notturno { color: var(--svc-notturno); background: var(--svc-notturno-bg); }
.prt__type.is-automotive { color: var(--svc-pickup-ink); background: var(--svc-pickup-bg); }

.prt__hqs { display: flex; align-items: center; gap: 8px; }
.prt__hqCount {
  font-size: 11px; font-weight: 700; color: var(--text-secondary);
  background: var(--bg-tertiary); border: 1px solid var(--border-color);
  border-radius: 5px; padding: 1px 7px; font-variant-numeric: tabular-nums;
}
.prt__hqNames { font-size: 12.5px; color: var(--text-tertiary); }

.prt__svcs { display: flex; gap: 5px; flex-wrap: wrap; }
.prt__svc {
  font-size: 11px; font-weight: 700; letter-spacing: 0.03em;
  border-radius: 5px; padding: 1px 7px;
}
.prt__svc.is-booking { color: var(--svc-pickup-ink); background: var(--svc-pickup-bg); }
.prt__svc.is-journey { color: var(--svc-fleet); background: var(--svc-fleet-bg); }

.prt__foot {
  display: flex; align-items: center; justify-content: space-between;
  gap: 12px; flex-wrap: wrap; padding: 12px 14px;
  border-top: 1px solid var(--border-color);
}
.prt__footInfo { font-size: 12.5px; color: var(--text-tertiary); }

.prt :deep(.el-table__row) { cursor: pointer; }
/* header chiaro: l'override globale impone un gradiente viola con !important */
.prt :deep(.el-table th.el-table__cell) {
  background: var(--bg-tertiary) !important;
  color: var(--text-tertiary) !important;
  font-size: 11px; font-weight: 700; letter-spacing: 0.05em;
  padding: 11px 12px; border-bottom: 1px solid var(--border-color);
}
</style>
